/**
 * MyProductsPage Component - Story 6.2: Create My Products Page
 * Main page component integrating products table with filters and search
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { AlertTriangle, Plus, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useProducts } from '../hooks/useProducts';
import { useProductFilters } from '../hooks/useProductFilters';
import { ProductsTable } from '../components/ProductsTable';
import { SellerDashboardLayout } from '../components/SellerDashboardLayout';
import { deleteProduct } from '../services/productsService';
import type {
  MyProductsPageProps,
  ProductDeleteRequest,
  ProductEditAction,
  ProductViewInquiriesAction,
  ProductStatus,
} from '../types/products.types';
import { STATUS_FILTER_OPTIONS } from '../constants/statusConstants';

/**
 * MyProductsPage Component
 * Complete product management page with table, filters, search, and actions
 *
 * @component
 * @example
 * <MyProductsPage
 *   title="My Products"
 *   onNavigateToEdit={(productId) => navigate(`/edit/${productId}`)}
 * />
 */
export const MyProductsPage: React.FC<MyProductsPageProps> = ({
  title = 'My Products',
  onNavigateToEdit,
  onNavigateToDetail,
  onNavigateToInquiries,
  disableLayout = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { filters, setFilters, updateStatus, updateSearch, updateSort, goToPage, resetFilters } =
    useProductFilters();
  const { products, isLoading, isError, error, totalPages, currentPage, refetch } =
    useProducts(filters);

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = React.useState<{
    isOpen: boolean;
    productId: string;
    productName: string;
  }>({
    isOpen: false,
    productId: '',
    productName: '',
  });

  /**
   * Refetch products when page mounts or when returning from other pages
   * This ensures inquiry counts are always up-to-date
   */
  React.useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /**
   * Handle status filter change
   */
  const handleStatusChange = React.useCallback(
    (status: string) => {
      updateStatus(status as 'ALL' | ProductStatus);
    },
    [updateStatus]
  );

  /**
   * Handle search input change
   */
  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSearch(e.target.value);
    },
    [updateSearch]
  );

  /**
   * Handle clear filters
   */
  const handleResetFilters = React.useCallback(() => {
    resetFilters();
    toast.success('Filters reset successfully');
  }, [resetFilters]);

  /**
   * Handle view product
   */
  const handleViewProduct = React.useCallback(
    (action: ProductEditAction) => {
      // Use the current route context
      const currentPath = location.pathname;
      if (currentPath.startsWith('/sales')) {
        navigate(`/sales/my-products/view/${action.productId}`);
      } else {
        navigate(`/marketplace/products/${action.productId}/view`);
      }
    },
    [navigate, location.pathname]
  );

  /**
   * Handle edit product
   */
  const handleEditProduct = React.useCallback(
    (action: ProductEditAction) => {
      if (onNavigateToEdit) {
        onNavigateToEdit(action.productId);
      } else {
        // Use the current route context
        const currentPath = location.pathname;
        if (currentPath.startsWith('/sales')) {
          navigate(`/sales/my-products/${action.productId}/edit`);
        } else {
          navigate(`/marketplace/products/${action.productId}/edit`);
        }
      }
    },
    [navigate, onNavigateToEdit, location.pathname]
  );

  /**
   * Handle view inquiries
   */
  const handleViewInquiries = React.useCallback(
    (action: ProductViewInquiriesAction) => {
      if (onNavigateToInquiries) {
        onNavigateToInquiries(action.productId);
      } else {
        // Use the current route context
        const currentPath = location.pathname;
        if (currentPath.startsWith('/sales')) {
          // In sales context, navigate to sales inquiries with productId filter
          navigate(`/sales/inquiries?productId=${action.productId}`);
        } else {
          navigate(`/marketplace/products/${action.productId}/inquiries`);
        }
      }
    },
    [navigate, onNavigateToInquiries, location.pathname]
  );

  /**
   * Handle row click (navigate to product detail)
   */
  const handleRowClick = React.useCallback(
    (productId: string) => {
      if (onNavigateToDetail) {
        onNavigateToDetail(productId);
      } else {
        // Use the current route context for navigation
        const currentPath = location.pathname;
        if (currentPath.startsWith('/sales')) {
          // In sales context, use view page instead of public detail
          navigate(`/sales/my-products/view/${productId}`);
        } else {
          navigate(`/marketplace/products/${productId}`);
        }
      }
    },
    [navigate, onNavigateToDetail, location.pathname]
  );

  /**
   * Handle delete product button click
   */
  const handleDeleteClick = React.useCallback((action: ProductDeleteRequest) => {
    setDeleteDialog({
      isOpen: true,
      productId: action.productId,
      productName: action.productName,
    });
  }, []);

  /**
   * Handle delete confirmation
   */
  const handleConfirmDelete = React.useCallback(async () => {
    try {
      await deleteProduct(deleteDialog.productId);
      setDeleteDialog((current) => ({ ...current, isOpen: false }));
      // Refresh the table
      refetch();
    } catch (error) {
      console.error('[MyProductsPage] Delete failed:', error);
      // Error is handled by the parent alert dialog
    }
  }, [deleteDialog.productId, refetch]);

  /**
   * Handle page change
   */
  const handlePageChange = React.useCallback(
    (page: number) => {
      goToPage(page);
    },
    [goToPage]
  );

  /**
   * Handle sort change
   */
  const handleSortChange = React.useCallback(
    (sortBy: string, sortOrder: 'asc' | 'desc') => {
      updateSort(sortBy as 'createdDate' | 'inquiryCount' | 'name', sortOrder);
    },
    [updateSort]
  );

  /**
   * Handle product status change (Story 6.6)
   * Optimistically update cached data then refetch for server-side truth
   */
  const handleProductStatusChange = React.useCallback(
    (productId: string, newStatus: ProductStatus) => {
      console.log('[MyProductsPage] Product status changed:', { productId, newStatus });

      // Optimistically update the product status in ALL cached product queries
      // This ensures the client-side filter in useProducts immediately removes/adds
      // the product from the correct filtered view (e.g., toggling INACTIVE→ACTIVE
      // removes it from the INACTIVE view right away, without waiting for refetch)
      queryClient.setQueriesData(
        { queryKey: ['products'] },
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((p: any) =>
              String(p.id) === String(productId)
                ? { ...p, status: newStatus }
                : p
            ),
          };
        }
      );

      // Also invalidate to refetch fresh data from server
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    [queryClient]
  );

  /**
   * Handle product status change error (Story 6.6)
   */
  const handleStatusError = React.useCallback(
    (productId: string, error: any) => {
      console.error('[MyProductsPage] Status change failed:', { productId, error });
      // Error handling is done in ProductStatusToggle component with rollback
      // but you can add additional error notification here if needed
    },
    []
  );

  /**
   * Navigate to add product page
   */
  const handleAddProduct = React.useCallback(() => {
    // Use the current route context - if we're in /sales, use sales routes
    const currentPath = location.pathname;
    if (currentPath.startsWith('/sales')) {
      navigate('/sales/my-products/add');
    } else {
      navigate('/marketplace/products/new');
    }
  }, [navigate, location.pathname]);

  // Content to render (with or without layout wrapper)
  const pageContent = (
    <>
      {/* Page header with add product button */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600 text-sm mt-1">Manage your products and respond to inquiries</p>
        </div>
        <Button
          onClick={handleAddProduct}
          className="bg-blue-600 hover:bg-blue-700"
          size="lg"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Filters and search section */}
      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search products..."
              value={filters.search}
              onChange={handleSearchChange}
              className="pl-10"
            />
          </div>

          {/* Status filter */}
          <Select value={filters.status} onValueChange={handleStatusChange}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Sort dropdown */}
          <Select
            value={filters.sortBy || 'createdDate'}
            onValueChange={(value) =>
              handleSortChange(value, filters.sortOrder || 'desc')
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="createdDate">Created Date</SelectItem>
              <SelectItem value="inquiryCount">Inquiry Count</SelectItem>
              <SelectItem value="name">Product Name</SelectItem>
            </SelectContent>
          </Select>

          {/* Reset filters button */}
          <Button
            variant="outline"
            onClick={handleResetFilters}
            className="flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Filters
          </Button>
        </div>

        {/* Active filters info - only show when filters differ from default (ACTIVE) */}
        {(filters.search || filters.status !== 'ACTIVE') && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              Showing results
              {filters.search && ` for "${filters.search}"`}
              {filters.status !== 'ACTIVE' && ` with status "${filters.status}"`}
              {(filters.search || filters.status !== 'ACTIVE') && (
                <button
                  onClick={handleResetFilters}
                  className="text-blue-600 hover:text-blue-700 font-medium ml-2 underline"
                >
                  Clear filters
                </button>
              )}
            </p>
          </div>
        )}
      </Card>

      {/* Products table */}
      <ProductsTable
        products={products}
        isLoading={isLoading}
        isError={isError}
        error={error}
        totalPages={totalPages || 1}
        currentPage={currentPage}
        onViewClick={handleViewProduct}
        onEditClick={handleEditProduct}
        onViewInquiriesClick={handleViewInquiries}
        onDeleteClick={handleDeleteClick}
        onStatusChange={handleProductStatusChange}
        onStatusError={handleStatusError}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        onRetry={() => refetch()}
        sortBy={filters.sortBy}
        onSortChange={handleSortChange}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(isOpen) =>
        setDeleteDialog((current) => ({ ...current, isOpen }))
      }>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Delete Product
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.productName}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">
              All associated inquiries will also be deleted.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  // Conditionally wrap content with SellerDashboardLayout
  if (disableLayout) {
    return pageContent;
  }

  return (
    <SellerDashboardLayout
      title={title}
      breadcrumbs={[
        { label: 'Dashboard', path: '/marketplace/seller/dashboard' },
        { label: 'My Products', path: '/marketplace/seller/products' },
      ]}
      isLoading={false}
      error={null}
    >
      {pageContent}
    </SellerDashboardLayout>
  );
};

export type { MyProductsPageProps };
